import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Package, Clock, Headphones, ArrowRight, Building2, CheckCircle2 } from "lucide-react";

const benefits = [
  { 
    icon: Package, 
    title: "Bulk & Project Pricing",
    description: "Competitive rates for large orders"
  },
  { 
    icon: Clock, 
    title: "Fast Quote Turnaround",
    description: "Response within 24–48 hours"
  },
  { 
    icon: Headphones, 
    title: "Priority Support",
    description: "Dedicated trade team assistance"
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

export function B2BSection() {
  return (
    <section className="relative py-20 md:py-28 overflow-hidden">
      {/* Background with subtle pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary via-secondary to-secondary/95" />
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      
      <div className="container relative z-10">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 mb-6">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">For Trade & Business</span>
            </div>
            
            <h2 className="mb-4 text-3xl font-bold tracking-tight text-secondary-foreground md:text-4xl lg:text-5xl">
              Trade & Bulk Enquiries
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-secondary-foreground/80">
              We support builders, landscapers, and developers with fast, project-based bulk quotes across{" "}
              <span className="font-semibold text-secondary-foreground">QLD, NSW, VIC & SA</span>.
            </p>
          </motion.div>

          {/* Benefits Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="grid gap-6 md:grid-cols-3 mb-12"
          >
            {benefits.map((benefit) => (
              <motion.div
                key={benefit.title}
                variants={itemVariants}
                className="group relative"
              >
                <div className="relative h-full rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 p-6 transition-all duration-300 hover:bg-white/15 hover:border-white/30 hover:-translate-y-1 hover:shadow-xl">
                  {/* Icon */}
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-accent/20 transition-all duration-300 group-hover:bg-accent group-hover:scale-110">
                    <benefit.icon className="h-7 w-7 text-accent transition-colors duration-300 group-hover:text-accent-foreground" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="mb-2 text-lg font-bold text-secondary-foreground">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-secondary-foreground/70">
                    {benefit.description}
                  </p>
                  
                  {/* Check mark */}
                  <div className="absolute top-4 right-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <CheckCircle2 className="h-5 w-5 text-accent" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-center"
          >
            <Button asChild variant="hero" size="xl" className="group shadow-2xl">
              <Link to="/bulk-quote">
                Request Trade Quote
                <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
            
            <p className="mt-4 text-sm text-secondary-foreground/60">
              No account needed — just submit your project details
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
